import { ArrowLeft, Edit } from "lucide-react";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getBooking,
  setBookingStatus,
  getAllGlamPackages,
} from "../../../services/api";

export function BookingDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null) as any;
  const [glamPackages, setGlamPackages] = useState([]) as any;
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger state

  useEffect(() => {
    if (!id) {
      toast.error("Invalid booking ID");
      navigate("/control/bookings");
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [bookingResponse, packagesResponse] = (await Promise.all([
          getBooking(id),
          getAllGlamPackages(),
        ])) as any;

        setBooking(bookingResponse.data.data);
        setGlamPackages(packagesResponse.data.data);
      } catch (error) {
        toast.error("Failed to fetch booking details");
        navigate("/control/bookings");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, refreshTrigger]);

  const handleStatusUpdate = async (newStatus: string) => {
    try {
      await setBookingStatus(booking.id, newStatus);
      toast.success(`Booking status updated to ${newStatus}!`, {
        position: "top-center",
        autoClose: 3000,
      });
      setRefreshTrigger((prev) => prev + 1); // Trigger refresh after status update
    } catch (error) {
      toast.error("Failed to update booking status");
    }
  };

  const handleEditClick = () => {
    if (booking && booking.id) {
      navigate(`/control/bookings/edit/${booking.id}`);
    } else {
      toast.error("Cannot edit booking: Invalid booking data");
    }
  };

  // Get glam package name
  const glamPackageName =
    glamPackages.find((pkg: any) => pkg.id === booking.glam_package_id)
      ?.title || "Unknown Package";

  // Calculate session fee (this would normally come from the API)
  const sessionFee =
    glamPackages.find((pkg: any) => pkg.id === booking.glam_package_id)
      ?.price || "$45";

  return (
    <div className="space-y-6">
      <ToastContainer />
      {/* Back Button */}
      <div className="flex items-center gap-2 text-[#B87775]">
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => navigate("/control/bookings")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="font-medium">BACK</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <Card className="p-8">
          <div className="grid lg:grid-cols-2 gap-8 relative">
            {/* Customer Image */}
            <div className="flex flex-col">
              <div className="aspect-square rounded-md overflow-hidden">
                {booking.media_url ? (
                  <img
                    src={booking.media_url || "/placeholder.svg"}
                    alt={booking.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    No Image
                  </div>
                )}
              </div>

              {/* Booking Details */}
              <div className="space-y-6">
                <div className="flex items-center justify-between mt-4">
                  <h2 className="text-2xl font-semibold">{booking.name}</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#B87775] text-[#B87775] hover:bg-rose-50"
                    onClick={handleEditClick}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>

                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-x-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Email Address
                      </p>
                      <p className="font-medium">{booking.email_address}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{booking.phone_number}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Glam Package
                      </p>
                      <p className="font-medium">{glamPackageName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Glam Spot</p>
                      <p className="font-medium">
                        {booking.glam_spot.includes("_session")
                          ? "Studio"
                          : booking.glam_spot.includes("_service")
                          ? "Home"
                          : booking.glam_spot}
                      </p>
                    </div>
                  </div>

                  <div>
                    {booking.glam_spot === "home_service" && (
                      <div className="grid grid-cols-2 gap-x-4">
                        <div className="">
                          <p className="text-sm text-muted-foreground">Home Address</p>
                          <p className="font-medium">{booking.home_address}</p>
                        </div>
                        {booking.landmark && (
                          <div className="">
                            <p className="text-sm text-muted-foreground">Landmark</p>
                            <p className="font-medium">{booking.landmark}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Number of Faces
                      </p>
                      <p className="font-medium">{booking.number_of_faces}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        First Experience
                      </p>
                      <p className="font-medium">
                        {booking.first_experience ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Glam Time</p>
                      <p className="font-medium">
                        {new Date(
                          booking.booking_start_date_time
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Glam Date</p>
                      <p className="font-medium">
                        {new Date(
                          booking.booking_start_date_time
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Confirmation Details */}
            <div className="sticky top-20 self-start h-[calc(100vh-200px)] overflow-y-auto">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-semibold mb-2">Confirmation Details</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  These information will be sent to customer's mail after
                  confirmation
                </p>

                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium">{booking.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Package</span>
                    <span className="font-medium">{glamPackageName}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Glam Time</span>
                    <span className="font-medium">
                      {new Date(
                        booking.booking_start_date_time
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Glam Date</span>
                    <span className="font-medium">
                      {new Date(
                        booking.booking_start_date_time
                      ).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Session Fee</span>
                    <span className="font-medium">${sessionFee}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">
                      Number of Faces
                    </span>
                    <span className="font-medium">
                      {booking.number_of_faces}
                    </span>
                  </div>

                  {/* Discount Section */}
                  {booking.featured_image_discount && (
                    <div className="flex items-start gap-3 mt-4 py-3 px-4 bg-white rounded-md">
                      <img src="/images/tenPercent.svg" alt="" />
                      <p className="text-sm">
                        Discount Applied: A customer approved their picture for
                        a public post, earning a 10% discount.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-8">
            {booking.status === "scheduled" ? (
              <>
                <Button
                  className="text-red-500 bg-red-50 hover:bg-red-50 hover:text-red-500"
                  onClick={() => handleStatusUpdate("declined")}
                >
                  CANCEL BOOKING
                </Button>
                <Button
                  className="bg-[#B87775] hover:bg-[#B87775]/90 text-white"
                  onClick={() => handleStatusUpdate("completed")}
                >
                  ACCEPT BOOKING
                </Button>
              </>
            ) : (
              <div className="flex gap-4 w-full">
                <Button
                  className="flex-1 bg-[#B87775] hover:bg-[#B87775]/90 text-white"
                  onClick={() => handleStatusUpdate("scheduled")}
                >
                  RE-SCHEDULE
                </Button>
                {booking.status === "completed" ? (
                  <Button
                    className="flex-1 bg-green-900 hover:bg-green-500/90 text-white"
                    disabled
                  >
                    COMPLETED
                  </Button>
                ) : (
                  <Button
                    className="flex-1 bg-red-700 hover:bg-red-500/90 text-white"
                    disabled
                  >
                    DECLINED
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
